import type { MembershipRole } from "@shyne/shared/api";
import { store } from "@/lib/store";
import { PermissionError } from "./permissions.error";
import { grants, permissionsOf } from "./role-permissions";
import type { uuid } from "@/interfaces";
import type { MemberOrganization } from "@/models/organization.model";
import { organization_user_status } from "@/models/organization-user.model";

export interface UserPermissions {
  role: MembershipRole;
  permissions: readonly string[];
}

export function permissionsIn(organizations: readonly MemberOrganization[], organizationId: uuid | null): UserPermissions | null {
  const membership = organizations.find((organization) => organization.id === organizationId && organization.status === organization_user_status.active);
  return membership ? { role: membership.role, permissions: permissionsOf(membership.role) } : null;
}

const currentUserId = (): uuid | undefined => store.getState().auth.user?.id;

class PermissionService {
  async getUserPermissions(): Promise<UserPermissions | null> {
    const { organizations, activeOrgId } = store.getState().organization;
    return permissionsIn(organizations, activeOrgId);
  }

  async hasPermission(permission: string): Promise<boolean> {
    const userPermissions = await this.getUserPermissions();
    return userPermissions !== null && grants(userPermissions.permissions, permission);
  }

  async requirePermission(permission: string): Promise<void> {
    if (!(await this.hasPermission(permission))) throw new PermissionError(permission, currentUserId());
  }
}

export const permissionService = new PermissionService();

export const requirePermission = (permission: string) => permissionService.requirePermission(permission);
